/**
 * Browser → server timezone sync.
 *
 * On first authenticated load, if the user's stored `timezone` is still the
 * `'UTC'` default and the browser knows a different IANA zone, PATCH it to
 * the browser's value. Subsequent loads short-circuit on the `synced` ref so
 * we don't re-send the same value every navigation.
 *
 * The server uses the stored value as the source of truth for any per-user
 * "local midnight" math (currently: the timers feature's overnight split).
 * We don't read `Intl` server-side because the cron backstop runs without a
 * request context.
 */
import { useMutation } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { usersApi } from '@/lib/api';
import { useMe } from '@/lib/me';

export function useTimezoneSync(): void {
  const { user } = useMe();
  const synced = useRef(false);
  const { mutate } = useMutation({
    mutationFn: (timezone: string) => usersApi.patchMe({ timezone }),
  });

  useEffect(() => {
    if (synced.current) return;
    if (!user) return;
    let browserTz: string | undefined;
    try {
      browserTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    } catch {
      return;
    }
    if (!browserTz || browserTz === 'UTC') return;
    if (user.timezone === browserTz) {
      // Already synced — flip the ref so this effect doesn't re-fire if
      // useMe refetches with the same data.
      synced.current = true;
      return;
    }
    // Only auto-sync when the stored value is still the UTC default.
    // Respect any user-set value (a future Settings → Timezone control).
    if (user.timezone !== 'UTC') {
      synced.current = true;
      return;
    }
    synced.current = true;
    mutate(browserTz);
    // `mutate` is a stable function reference from useMutation; using the
    // whole mutation object here would re-fire the effect on every state
    // flip (isPending → false, etc.).
  }, [user, mutate]);
}
